import React from 'react';
import { Check, AlertCircle, Lightbulb, Target } from 'lucide-react';
import { ExampleBadge } from './Section';

const EXAMPLE = {
  role: 'Performance Marketing Manager',
  score: 94,
  alignment: 'Strong',
  matched: ['Google Ads', 'Meta Ads', 'ROAS', 'A/B testing', 'GA4', 'Landing pages'],
  missing: ['Attribution modelling', 'Budget forecasting', 'Marketing mix'],
  suggestions: ['Add a bullet on attribution or MMM work if you have it', 'Quantify budget size managed per quarter', 'Mention forecasting tools used', 'Move “ROAS +41%” into your summary', 'Use “paid acquisition” alongside “performance marketing”'],
};

const Stat = ({ label, value, tone = 'text-primary' }) => (
  <div className="rounded-xl border border-border bg-white p-4">
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className={`mt-1 text-2xl font-bold ${tone}`}>{value}</div>
  </div>
);

// Marketing visual of the real job-match output (/api/ats/score with job_description). Clearly labelled as an example.
export const JobMatch = () => (
  <div className="rounded-2xl border border-border bg-surface p-3 sm:p-4" data-testid="job-match-visual">
    <div className="rounded-xl border border-border bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Target className="h-3.5 w-3.5" aria-hidden="true" /> Target role</div>
          <div className="mt-1 text-lg font-bold text-primary">{EXAMPLE.role}</div>
        </div>
        <ExampleBadge>Example output</ExampleBadge>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-4">
        <div className="flex items-center gap-4 rounded-xl border border-border bg-white p-4 sm:col-span-1">
          <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
            <svg className="h-16 w-16 -rotate-90" viewBox="0 0 100 100" aria-hidden="true"><circle cx="50" cy="50" r="42" fill="none" stroke="#E5E7EB" strokeWidth="9" /><circle cx="50" cy="50" r="42" fill="none" stroke="#059669" strokeWidth="9" strokeLinecap="round" strokeDasharray={`${EXAMPLE.score * 2.64} 264`} /></svg>
            <div className="absolute text-lg font-bold text-primary">{EXAMPLE.score}%</div>
          </div>
          <div className="text-xs font-medium text-muted-foreground">Resume<br />match</div>
        </div>
        <Stat label="Keyword alignment" value={EXAMPLE.alignment} tone="text-emerald-600" />
        <Stat label="Missing keywords" value={EXAMPLE.missing.length} tone="text-amber-600" />
        <Stat label="Suggested improvements" value={EXAMPLE.suggestions.length} />
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <div>
          <h4 className="text-sm font-semibold text-primary">Keywords</h4>
          <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Matched keywords">
            {EXAMPLE.matched.map((k) => <li key={k} className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700"><Check className="h-3 w-3" aria-hidden="true" />{k}</li>)}
            {EXAMPLE.missing.map((k) => <li key={k} className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700"><AlertCircle className="h-3 w-3" aria-hidden="true" />{k}</li>)}
          </ul>
        </div>
        <div>
          <h4 className="flex items-center gap-1.5 text-sm font-semibold text-primary"><Lightbulb className="h-4 w-4 text-accent" aria-hidden="true" /> Suggested improvements</h4>
          <ul className="mt-2 space-y-1.5">
            {EXAMPLE.suggestions.slice(0, 3).map((s) => <li key={s} className="flex items-start gap-2 text-sm text-primary/80"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />{s}</li>)}
          </ul>
        </div>
      </div>
    </div>
  </div>
);
